import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Scale, CheckCircle2, Award, Shield, GraduationCap, Globe } from 'lucide-react';

const About = () => {
  const { t, direction } = useLanguage();

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A]/30 to-transparent" />
      <div className="absolute top-20 right-0 w-64 h-64 bg-[#C9A55A]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-[#0A1628]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#0A1628]/5 border border-[#0A1628]/10 rounded-full mb-6">
            <Scale className="w-4 h-4 text-[#C9A55A]" />
            <span className="text-sm font-semibold text-[#0A1628] uppercase tracking-wider">
              {t.ui.aboutLawyer}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.about.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent mx-auto" />
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Image Section */}
          <div className={`relative ${direction === 'rtl' ? 'order-2' : ''}`}>
            {/* Decorative Frame */}
            <div className="absolute -inset-4 bg-gradient-to-br from-[#C9A55A]/20 to-[#0A1628]/20 rounded-3xl transform rotate-3" />
            <div className="absolute -inset-4 bg-gradient-to-tl from-[#C9A55A]/10 to-transparent rounded-3xl transform -rotate-3" />
            
            {/* Main Image Container */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <img 
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80" 
                alt="Law Office"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/90 via-[#0A1628]/40 to-transparent" />
              
              {/* Experience Badge */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-5 rounded-xl shadow-xl border border-[#C9A55A]/20">
                <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <div className="w-16 h-16 bg-gradient-to-br from-[#C9A55A] to-[#8B7355] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <div className={direction === 'rtl' ? 'text-right' : ''}>
                    <p className="text-3xl font-bold bg-gradient-to-r from-[#C9A55A] to-[#8B7355] bg-clip-text text-transparent" style={{ fontFamily: 'Georgia, serif' }}>
                      15+
                    </p>
                    <p className="text-sm text-gray-700 font-semibold">
                      {t.ui.yearsExperience ? `${t.ui.yearsExperience}` : (direction === 'rtl' ? 'سنوات من الخبرة القانونية الدولية' : 'Years of International Legal Experience')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-2xl shadow-xl flex items-center justify-center transform rotate-12 hover:rotate-0 transition-transform duration-300">
              <div className="text-center text-white">
                <Globe className="w-8 h-8 mx-auto mb-1 text-[#C9A55A]" />
                <p className="text-xs">International</p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className={`space-y-6 ${direction === 'rtl' ? 'text-right order-1' : ''}`}>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t.about.description}
            </p>

            {/* Expertise List */}
            <div className="space-y-3 pt-4">
              <h3 className={`text-xl font-bold text-[#0A1628] flex items-center gap-3 mb-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Shield className="w-5 h-5 text-[#C9A55A]" />
                {t.ui.areasOfExpertise}
              </h3>
              {t.about.expertise.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-[#C9A55A]/5 transition-colors border border-transparent hover:border-[#C9A55A]/20 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}
                >
                  <CheckCircle2 className="w-5 h-5 text-[#C9A55A] mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700 font-medium">{item}</p>
                </div>
              ))}
            </div>

            {/* Certifications */}
            <div className="pt-6">
              <h3 className={`text-xl font-bold text-[#0A1628] flex items-center gap-3 mb-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <GraduationCap className="w-5 h-5 text-[#C9A55A]" />
                {t.about.certifications}
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {t.about.certs.map((cert, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center gap-2 p-3 bg-[#0A1628]/5 rounded-lg border border-[#0A1628]/10 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className="w-2 h-2 bg-[#C9A55A] rounded-full flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">{cert}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
